import { create } from 'zustand'
import type React from 'react'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined'
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined'
import RoofingOutlinedIcon from '@mui/icons-material/RoofingOutlined'
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined'
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined'
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import SettingsInputAntennaOutlinedIcon from '@mui/icons-material/SettingsInputAntennaOutlined'

// ─── Icon registry ──────────────────────────────────────────────────────────

export type IconKey =
  | 'business' | 'apartment' | 'layers' | 'meetingRoom' | 'squareFoot'
  | 'gridView' | 'dashboard' | 'factory' | 'warehouse' | 'storefront'
  | 'car' | 'gas' | 'homeWork' | 'terminal' | 'viewQuilt' | 'domain'
  | 'build' | 'construction' | 'roofing' | 'kitchen' | 'shipping'
  | 'parking' | 'lightbulb' | 'electric' | 'water' | 'spa'
  | 'science' | 'fitness' | 'school' | 'restaurant' | 'medical' | 'antenna'

export const ICON_REGISTRY: Record<IconKey, { label: string; Component: React.ElementType }> = {
  business:     { label: 'Business',     Component: BusinessOutlinedIcon },
  apartment:    { label: 'Apartment',    Component: ApartmentOutlinedIcon },
  layers:       { label: 'Layers',       Component: LayersOutlinedIcon },
  meetingRoom:  { label: 'Meeting room', Component: MeetingRoomOutlinedIcon },
  squareFoot:   { label: 'Square foot',  Component: SquareFootOutlinedIcon },
  gridView:     { label: 'Grid',         Component: GridViewOutlinedIcon },
  dashboard:    { label: 'Dashboard',    Component: DashboardOutlinedIcon },
  factory:      { label: 'Factory',      Component: FactoryOutlinedIcon },
  warehouse:    { label: 'Warehouse',    Component: WarehouseOutlinedIcon },
  storefront:   { label: 'Storefront',   Component: StorefrontOutlinedIcon },
  car:          { label: 'Car',          Component: DirectionsCarOutlinedIcon },
  gas:          { label: 'Gas',          Component: LocalGasStationOutlinedIcon },
  homeWork:     { label: 'Home/work',    Component: HomeWorkOutlinedIcon },
  terminal:     { label: 'Terminal',     Component: TerminalOutlinedIcon },
  viewQuilt:    { label: 'Quilt',        Component: ViewQuiltOutlinedIcon },
  domain:       { label: 'Domain',       Component: DomainOutlinedIcon },
  build:        { label: 'Tools',        Component: BuildOutlinedIcon },
  construction: { label: 'Construction', Component: ConstructionOutlinedIcon },
  roofing:      { label: 'Roofing',      Component: RoofingOutlinedIcon },
  kitchen:      { label: 'Kitchen',      Component: KitchenOutlinedIcon },
  shipping:     { label: 'Shipping',     Component: LocalShippingOutlinedIcon },
  parking:      { label: 'Parking',      Component: LocalParkingOutlinedIcon },
  lightbulb:    { label: 'Lightbulb',    Component: LightbulbOutlinedIcon },
  electric:     { label: 'Electric',     Component: ElectricBoltOutlinedIcon },
  water:        { label: 'Water',        Component: WaterDropOutlinedIcon },
  spa:          { label: 'Spa',          Component: SpaOutlinedIcon },
  science:      { label: 'Lab',          Component: ScienceOutlinedIcon },
  fitness:      { label: 'Gym',          Component: FitnessCenterOutlinedIcon },
  school:       { label: 'School',       Component: SchoolOutlinedIcon },
  restaurant:   { label: 'Restaurant',   Component: RestaurantOutlinedIcon },
  medical:      { label: 'Medical',      Component: MedicalServicesOutlinedIcon },
  antenna:      { label: 'Antenna',      Component: SettingsInputAntennaOutlinedIcon },
}

// ─── Colour registry — subtle, muted pastels ───────────────────────────────

export type ColorKey =
  | 'indigo' | 'amber' | 'emerald' | 'sky' | 'violet'
  | 'rose' | 'slate' | 'teal' | 'red' | 'orange'
  | 'lime' | 'pink' | 'blue' | 'stone'

export interface ColorSwatch {
  name: string
  bg: string      // icon-tile background (resting)
  bgSel: string   // icon-tile background (selected/active)
  fg: string      // icon fill
  rail: string    // thin rail colour for tree guide lines
}

export const COLOR_REGISTRY: Record<ColorKey, ColorSwatch> = {
  indigo:  { name: 'Indigo',  bg: 'rgba(13,148,136,0.09)',   bgSel: 'rgba(13,148,136,0.16)',   fg: '#0D9488', rail: 'rgba(13,148,136,0.25)' },
  amber:   { name: 'Amber',   bg: 'rgba(217,119,6,0.10)',   bgSel: 'rgba(217,119,6,0.18)',   fg: '#D97706', rail: 'rgba(217,119,6,0.25)' },
  emerald: { name: 'Emerald', bg: 'rgba(5,150,105,0.09)',   bgSel: 'rgba(5,150,105,0.16)',   fg: '#059669', rail: 'rgba(5,150,105,0.25)' },
  sky:     { name: 'Sky',     bg: 'rgba(2,132,199,0.09)',   bgSel: 'rgba(2,132,199,0.16)',   fg: '#0284C7', rail: 'rgba(2,132,199,0.25)' },
  violet:  { name: 'Violet',  bg: 'rgba(124,58,237,0.09)',  bgSel: 'rgba(124,58,237,0.16)',  fg: '#7C3AED', rail: 'rgba(124,58,237,0.25)' },
  rose:    { name: 'Rose',    bg: 'rgba(225,29,72,0.09)',   bgSel: 'rgba(225,29,72,0.16)',   fg: '#E11D48', rail: 'rgba(225,29,72,0.25)' },
  slate:   { name: 'Slate',   bg: 'rgba(100,116,139,0.11)', bgSel: 'rgba(100,116,139,0.18)', fg: '#475569', rail: 'rgba(100,116,139,0.30)' },
  teal:    { name: 'Teal',    bg: 'rgba(13,148,136,0.09)',  bgSel: 'rgba(13,148,136,0.16)',  fg: '#0D9488', rail: 'rgba(13,148,136,0.25)' },
  red:     { name: 'Red',     bg: 'rgba(220,38,38,0.09)',   bgSel: 'rgba(220,38,38,0.16)',   fg: '#DC2626', rail: 'rgba(220,38,38,0.25)' },
  orange:  { name: 'Orange',  bg: 'rgba(234,88,12,0.09)',   bgSel: 'rgba(234,88,12,0.16)',   fg: '#EA580C', rail: 'rgba(234,88,12,0.25)' },
  lime:    { name: 'Lime',    bg: 'rgba(101,163,13,0.10)',  bgSel: 'rgba(101,163,13,0.18)',  fg: '#65A30D', rail: 'rgba(101,163,13,0.25)' },
  pink:    { name: 'Pink',    bg: 'rgba(219,39,119,0.09)',  bgSel: 'rgba(219,39,119,0.16)',  fg: '#DB2777', rail: 'rgba(219,39,119,0.25)' },
  blue:    { name: 'Blue',    bg: 'rgba(37,99,235,0.09)',   bgSel: 'rgba(37,99,235,0.16)',   fg: '#2563EB', rail: 'rgba(37,99,235,0.25)' },
  stone:   { name: 'Stone',   bg: 'rgba(120,113,108,0.11)', bgSel: 'rgba(120,113,108,0.18)', fg: '#57534E', rail: 'rgba(120,113,108,0.30)' },
}

// ─── Category type + store ──────────────────────────────────────────────────

export interface Category {
  id: string           // e.g. 'site', 'building', 'wing' (custom)
  label: string        // Display singular
  plural: string       // Display plural
  iconKey: IconKey
  colorKey: ColorKey
  isSystem: boolean    // Built-in categories can be edited (icon/colour) but not deleted.
}

interface Store {
  categories: Category[]
  add: (input: { label: string; plural: string; iconKey: IconKey; colorKey: ColorKey }) => Category
  update: (id: string, patch: Partial<Pick<Category, 'label' | 'plural' | 'iconKey' | 'colorKey'>>) => void
  remove: (id: string) => boolean
}

export const useCategoriesStore = create<Store>((set, get) => ({
  categories: [
    { id: 'building', label: 'Building', plural: 'Buildings', iconKey: 'apartment',    colorKey: 'amber',   isSystem: true },
    { id: 'floor',    label: 'Floor',    plural: 'Floors',    iconKey: 'layers',       colorKey: 'emerald', isSystem: true },
    { id: 'wing',     label: 'Wing',     plural: 'Wings',     iconKey: 'domain',       colorKey: 'sky',     isSystem: true },
    { id: 'room',     label: 'Room',     plural: 'Rooms',     iconKey: 'meetingRoom',  colorKey: 'violet',  isSystem: true },
    { id: 'area',     label: 'Area',     plural: 'Areas',     iconKey: 'gridView',     colorKey: 'rose',    isSystem: true },
    { id: 'zone',     label: 'Zone',     plural: 'Zones',     iconKey: 'dashboard',    colorKey: 'slate',   isSystem: true },
    { id: 'outdoor',  label: 'Outdoor',  plural: 'Outdoor',   iconKey: 'spa',          colorKey: 'lime',    isSystem: true },
    { id: 'other',    label: 'Other',    plural: 'Other',     iconKey: 'domain',       colorKey: 'stone',   isSystem: true },
  ],
  add: (input) => {
    const existing = new Set(get().categories.map((c) => c.id))
    let id = slugify(input.label)
    let n = 2
    while (existing.has(id)) {
      id = `${slugify(input.label)}-${n++}`
    }
    const created: Category = { id, ...input, isSystem: false }
    set((s) => ({ categories: [...s.categories, created] }))
    return created
  },
  update: (id, patch) => set((s) => ({
    categories: s.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)),
  })),
  remove: (id) => {
    const cat = get().categories.find((c) => c.id === id)
    if (!cat || cat.isSystem) return false
    set((s) => ({ categories: s.categories.filter((c) => c.id !== id) }))
    return true
  },
}))

// ─── Lookup helpers ─────────────────────────────────────────────────────────

export interface CategoryView {
  id: string
  label: string
  plural: string
  Icon: React.ElementType
  color: ColorSwatch
}

const FALLBACK: CategoryView = {
  id: '__unknown__',
  label: 'Location',
  plural: 'Locations',
  Icon: DomainOutlinedIcon,
  color: COLOR_REGISTRY.slate,
}

/**
 * Non-reactive lookup — use from memos or derived data.
 * For subscribing components prefer `useCategoryView` below.
 */
export function getCategoryView(
  categories: Category[], id: string,
): CategoryView {
  const cat = categories.find((c) => c.id === id)
  if (!cat) return FALLBACK
  return {
    id: cat.id,
    label: cat.label,
    plural: cat.plural,
    Icon: ICON_REGISTRY[cat.iconKey].Component,
    color: COLOR_REGISTRY[cat.colorKey],
  }
}

export function useCategoryView(id: string): CategoryView {
  const categories = useCategoriesStore((s) => s.categories)
  return getCategoryView(categories, id)
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function slugify(s: string): string {
  const base = s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  return base || 'category'
}
